import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

import { CartArticle, CartObject } from '../../reward-shop/types';
import BonusPoints from '../services/BonusPoints';
import Cart from '../services/Cart';
import { useDeferredPromise } from '../utils/customHooks';
import { RewardShopArticle, BonusPointsOverview } from '../utils/types';

import RewardShopContext from './RewardShopContext';

export interface CartEventsContextProps {
  onAddToCart: (articleId: number, quantityToAdd: number, node: any) => void;
  isPromotionalArticle: (article: RewardShopArticle) => boolean;
  cartArticles: CartArticle[];
  sidCookie: string;
  isLogged: boolean;
  standardArticles: RewardShopArticle[];
  donationArticles: RewardShopArticle[];
  redeemableBalance: number | null;
  articlesHighlighted: RewardShopArticle[];
}

const CartEventsContext = createContext<Partial<CartEventsContextProps>>({});

export const CartEventsProvider: React.FC<any> = ({ children, ...props }): JSX.Element => {
  const { allArticles, bonusPointsOverview } = useContext(RewardShopContext);
  const isLogged = props.view === 'private';
  const [redeemableBalance, setRedeemableBalance] = useState<number>(0);
  const [cartBPs, setCartBPs] = useState<number | null>(null);
  const [cartArticles, setCartArticles] = useState<CartArticle[]>([]);
  const [sidCookie, setSidCookie] = useState<string>();
  const [standardArticles, setStandardArticles] = useState<RewardShopArticle[]>([]);
  const [donationArticles, setDonationArticles] = useState<RewardShopArticle[]>([]);
  const [articlesHighlighted, setArticlesHighlighted] = useState<RewardShopArticle[]>([]);
  const { defer } = useDeferredPromise<any>();

  const onBalanceGet = useCallback(
    (overview: BonusPointsOverview) => {
      if ((cartBPs || cartBPs === 0) && overview) {
        setRedeemableBalance(Math.max(overview.balanceActive - cartBPs, 0));
      }
    },
    [cartBPs, bonusPointsOverview],
  );

  useEffect(() => {
    BonusPoints.init();
    // @ts-ignore
    window['changeArticleQuantityDeferred'] = defer().promise;
    Cart.onCartGet(
      (eCart: any, cartData: any) => onCartGet(cartData.response),
      () => {},
    );

    Cart.onSetArticleQuantity(
      () => onSetArticleQuantity(),
      () => onSetArticleQuantityError(),
    );
  }, []);

  useEffect(() => {
    BonusPoints.onBalanceGetOne(
      () => onBalanceGet(bonusPointsOverview!),
      () => {},
    );
  }, [onBalanceGet]);

  useEffect(() => {
    if (allArticles) {
      Cart.notifyQuantityChange((eventName: string, data: any) =>
        notifyQuantityChange(eventName, data),
      );
    }
  }, [allArticles]);

  useEffect(() => {
    if (allArticles) {
      allArticles.forEach((article) => {
        if (cartArticles) {
          const a = cartArticles.find((a) => a.id === article.articleId);
          article.quantityInCart = a !== undefined ? a.quantity : 0;
        } else {
          article.quantityInCart = 0;
        }
      });
    }
  }, [cartArticles, allArticles]);

  const notifyQuantityChange = (eventName: string, data: any) => {
    const { price, quantity, shopId, psId, bestPriceArticle, cartId, sellerType } = data;
    const { bonusPointsArticlePromoted, bonusPointsArticleHighlighted, bonusPoints } =
      bonusPointsArticleFeatures(Number.parseInt(psId));
    // @ts-ignore
    window['changeArticleQuantityDeferred'].notify(
      psId,
      shopId,
      quantity,
      price,
      cartId,
      bestPriceArticle,
      bonusPointsArticlePromoted,
      bonusPointsArticleHighlighted,
      bonusPoints,
      sellerType
    );
  };

  const bonusPointsArticleFeatures = (
    psId: number,
  ): {
    bonusPointsArticlePromoted: string;
    bonusPointsArticleHighlighted: string;
    bonusPoints: number;
  } => {
    const article: RewardShopArticle = (allArticles ?? []).filter(
      (article) => article.shopIdentifier === psId,
    )[0];
    const bonusPointsArticlePromoted = article.promotionalPrice
      ? 'bpArticlePromoted'
      : 'bpArticleNotPromoted';
    const bonusPointsArticleHighlighted = article.articleHighlighted
      ? 'bpArticleHighlighted'
      : 'bpArticleNotHighlighted';
    return {
      bonusPointsArticlePromoted: bonusPointsArticlePromoted,
      bonusPointsArticleHighlighted: bonusPointsArticleHighlighted,
      bonusPoints: article?.currentPoints ?? 0,
    };
  };

  const onCartGet = (cart: CartObject): void => {
    setSidCookie(cart.sid);
    setCartArticles(cart.articles);
    setCartBPs(cart?.bonusPoints);
  };

  useEffect(() => {
    if (redeemableBalance || redeemableBalance === 0) {
      BonusPoints.getBalance();
    } else {
      onBalanceGet(bonusPointsOverview!);
    }
  }, [cartBPs, bonusPointsOverview]);

  const onSetArticleQuantity = () => {
    Cart.getCart();
  };

  const onSetArticleQuantityError = (error?: any) => {
    console.error('Unable to add to cart: ', error);
    Cart.getCart();
  };

  const onAddToCart = (articleId: number, quantityToAdd: number, node: any): void => {
    Cart.setArticleQuantity(articleId, quantityToAdd, node);
  };

  const isPromotionalArticle = (article: RewardShopArticle): boolean => {
    return (!!article?.promotionalPrice && !!article.points) ?? false;
  };

  const getOriginalPoints = (article: RewardShopArticle): number => {
    return article.points;
  };

  const getFinalPoints = (article: RewardShopArticle): number => {
    return isPromotionalArticle(article) ? article.promotionalPrice! : getOriginalPoints(article);
  };

  const sortArticles = (): RewardShopArticle[] => {
    allArticles?.map((article) => {
      article.originalPoints = getOriginalPoints(article);
      article.currentPoints = getFinalPoints(article);
    });
    return allArticles?.sort((a, b) => a.currentPoints! - b.currentPoints!) ?? [];
  };

  useEffect(() => {
    if (allArticles) {
      setStandardArticles(sortArticles());
      const donationArticles = allArticles.filter((a) => a.articleType === 'Donation');
      setDonationArticles(donationArticles);
    }
  }, [allArticles]);

  useEffect(() => {
    if (standardArticles.length) {
      const articlesHighlighted = standardArticles.filter((a) => a.articleHighlighted).slice(0, 4);
      setArticlesHighlighted(articlesHighlighted);
    }
  }, [standardArticles]);

  return (
    <CartEventsContext.Provider
      value={{
        onAddToCart,
        isPromotionalArticle,
        cartArticles,
        sidCookie,
        isLogged,
        standardArticles,
        donationArticles,
        redeemableBalance,
        articlesHighlighted,
      }}>
      {children}
    </CartEventsContext.Provider>
  );
};
export default CartEventsContext;
